import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { ResponsiveBar } from '@nivo/bar';
import { DeepAnalysisUpsellSummary, DeepAnalysisUpsellValuesByTag } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import { nivoChartTheme } from '../../../../../shared/constants/chartTheme';

export interface UpsellSummaryEntry {
    applicationId: string;
    summary: DeepAnalysisUpsellSummary;
}

interface UpsellAnalyticsDashboardProps {
    summaries: UpsellSummaryEntry[];
    successCountByItem: Record<string, number>;
    hiddenItemKeys: Set<string>;
    upsellAttempts: number;
    selectedLocationCount: number;
    valueConfigByEntity: Record<string, DeepAnalysisUpsellValuesByTag>;
    onSaveItemValue: (tag: string, dollarValue: number) => Promise<void>;
    isLoading: boolean;
    startDate: string;
    endDate: string;
    appNameMap?: Record<string, string>;
    onBack: () => void;
}

type SortMode = 'count-desc' | 'count-asc' | 'revenue-desc' | 'revenue-asc';
type TemporalMode = 'days' | 'hours';

interface UpsellItem {
    key: string;
    name: string;
    count: number;
    revenue: number;
}

interface UpsellSession {
    applicationId: string;
    sessionId: string;
    callTime: string;
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ITEM_COLORS = ['#0284C7', '#0F766E', '#7C3AED', '#EA580C', '#DB2777', '#4F46E5', '#65A30D', '#0891B2', '#9333EA', '#C2410C'];
const PAGE_SIZE = 20;
const RevenueTooltip = styled.div<{ $visible: boolean }>`background: #1e293b; border-radius: 8px; box-shadow: 0 8px 20px rgba(15, 23, 42, .18); color: #fff; font-size: 12px; font-weight: 500; left: 50%; line-height: 1.4; opacity: ${props => props.$visible ? 1 : 0}; padding: 9px 12px; pointer-events: none; position: absolute; text-align: center; top: -42px; transform: translateX(-50%); transition: opacity .12s ease; white-space: nowrap; z-index: 30;`;
const ValueEditor = styled.div`align-items: center; display: flex; gap: 6px; justify-content: flex-end; min-width: 160px;`;
const CurrencyInput = styled.input`border: 1px solid #cbd5e1; border-radius: 6px; color: #334155; font: inherit; font-size: 12px; min-width: 0; padding: 6px 7px; width: 88px; &:focus { border-color: #0284c7; outline: 2px solid rgba(2, 132, 199, .12); } &:disabled { background: #f8fafc; cursor: wait; }`;
const SaveValueButton = styled.button`background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 6px; color: #0369a1; cursor: pointer; font: inherit; font-size: 11px; font-weight: 600; padding: 6px 8px; white-space: nowrap; &:hover { background: #dbeafe; } &:disabled { cursor: wait; opacity: .6; }`;
const SaveError = styled.div`color: #b91c1c; font-size: 10px; margin-top: 4px; max-width: 180px; text-align: right;`;

const normalize = (value: string) => value.toLocaleLowerCase();
const formatCurrency = (value: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value || 0);
const truncateChartLabel = (value: string) => value.length > 15 ? `${value.slice(0, 15)}…` : value;
const formatItemName = (tag: string) => tag
    .replace(/-/g, ' ')
    .split(' ')
    .filter(word => word && word.toLocaleLowerCase() !== 'upsold')
    .map(word => `${word.charAt(0).toLocaleUpperCase()}${word.slice(1).toLocaleLowerCase()}`)
    .join(' ');

const UpsellAnalyticsDashboard: React.FC<UpsellAnalyticsDashboardProps> = ({
    summaries,
    successCountByItem,
    hiddenItemKeys,
    upsellAttempts,
    selectedLocationCount,
    valueConfigByEntity,
    onSaveItemValue,
    isLoading,
    startDate,
    endDate,
    appNameMap,
    onBack
}) => {
    const [sortMode, setSortMode] = useState<SortMode>('count-desc');
    const [temporalMode, setTemporalMode] = useState<TemporalMode>('days');
    const [selectedItem, setSelectedItem] = useState<string | null>(null);
    const [selectedItemTag, setSelectedItemTag] = useState<string | null>(null);
    const [excludedItemKeys, setExcludedItemKeys] = useState<Set<string>>(new Set());
    const [draftExcludedItemKeys, setDraftExcludedItemKeys] = useState<Set<string>>(new Set());
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [oldestFirst, setOldestFirst] = useState(false);
    const [page, setPage] = useState(0);
    const [isRevenueTooltipVisible, setIsRevenueTooltipVisible] = useState(false);
    const revenueTooltipRef = useRef<HTMLDivElement>(null);
    const [valueDrafts, setValueDrafts] = useState<Record<string, string>>({});
    const [savingValueTag, setSavingValueTag] = useState<string | null>(null);
    const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

    const configuredValuesByItem = useMemo(() => {
        const valuesByItem = new Map<string, number[]>();
        Object.values(valueConfigByEntity).forEach(config => {
            Object.entries(config || {}).forEach(([tag, value]) => {
                const key = normalize(tag);
                const values = valuesByItem.get(key) || [];
                values.push(Number(value?.dollarValue) || 0);
                valuesByItem.set(key, values);
            });
        });
        return valuesByItem;
    }, [valueConfigByEntity]);

    const items = useMemo(() => {
        const byName = new Map<string, UpsellItem>();
        summaries.forEach(({ summary }) => {
            (summary.tagsTotals || []).forEach(({ tag, count }) => {
                const key = normalize(tag);
                if (hiddenItemKeys.has(key)) return;
                const item = byName.get(key) || { key: tag, name: formatItemName(tag), count: 0, revenue: 0 };
                item.count += count || 0;
                byName.set(key, item);
            });
            (summary.upsellValues || []).forEach(value => {
                const name = value['upsold-item'];
                if (!name) return;
                const key = normalize(name);
                if (hiddenItemKeys.has(key)) return;
                const item = byName.get(key) || { key: name, name: formatItemName(name), count: 0, revenue: 0 };
                item.revenue += value['total-dollar-value'] || 0;
                byName.set(key, item);
            });
        });
        byName.forEach((item, key) => {
            if (successCountByItem[key] !== undefined) item.count = successCountByItem[key];
        });
        return Array.from(byName.values()).sort((a, b) => {
            if (sortMode === 'count-asc') return a.count - b.count || a.name.localeCompare(b.name);
            if (sortMode === 'revenue-desc') return b.revenue - a.revenue || b.count - a.count;
            if (sortMode === 'revenue-asc') return a.revenue - b.revenue || a.count - b.count;
            return b.count - a.count || a.name.localeCompare(b.name);
        });
    }, [summaries, sortMode, successCountByItem, hiddenItemKeys]);

    const visibleItems = useMemo(() => items.filter(item => !excludedItemKeys.has(normalize(item.key))), [items, excludedItemKeys]);
    const successfulUpsells = visibleItems.reduce((total, item) => total + item.count, 0);
    const upsellSuccessRate = upsellAttempts > 0 ? successfulUpsells / upsellAttempts * 100 : 0;
    const totalRevenue = visibleItems.reduce((total, item) => total + item.revenue, 0);
    const averageUpsellRevenue = successfulUpsells > 0 ? totalRevenue / successfulUpsells : 0;
    const averageRevenuePerLocation = selectedLocationCount > 0 ? totalRevenue / selectedLocationCount : 0;
    const topItems = useMemo(() => [...visibleItems].sort((a, b) => b.count - a.count).slice(0, 10), [visibleItems]);
    const temporalItems = useMemo(() => [...visibleItems].sort((a, b) => b.count - a.count).slice(0, 5), [visibleItems]);

    const temporalData = useMemo(() => {
        const buckets = temporalMode === 'days' ? DAYS : Array.from({ length: 24 }, (_, hour) => hour);
        const rows = buckets.map(bucket => {
            const row: Record<string, string | number> = {
                bucket: temporalMode === 'days' ? bucket as string : `${(bucket as number) % 12 || 12} ${(bucket as number) < 12 ? 'AM' : 'PM'}`
            };
            temporalItems.forEach(item => { row[item.name] = 0; });
            let bucketTotal = 0;
            summaries.forEach(({ summary }) => {
                const byBucket = temporalMode === 'days' ? summary.byDayOfWeek : summary.byHourOfDay;
                const key = String(bucket);
                (byBucket?.[key] || []).forEach(({ tag, count }) => {
                    const visibleItem = visibleItems.find(item => normalize(item.key) === normalize(tag));
                    if (!visibleItem) return;
                    const itemCount = count || 0;
                    bucketTotal += itemCount;
                    if (temporalItems.some(item => normalize(item.key) === normalize(tag))) {
                        row[visibleItem.name] = Number(row[visibleItem.name] || 0) + itemCount;
                    }
                });
            });
            return { row, bucketTotal };
        });
        if (temporalMode === 'days') return rows.map(({ row }) => row);
        const firstPopulatedHour = rows.findIndex(({ bucketTotal }) => bucketTotal > 0);
        if (firstPopulatedHour < 0) return [];
        let lastPopulatedHour = rows.length - 1;
        while (lastPopulatedHour > firstPopulatedHour && rows[lastPopulatedHour].bucketTotal === 0) lastPopulatedHour--;
        return rows.slice(firstPopulatedHour, lastPopulatedHour + 1).map(({ row }) => row);
    }, [summaries, temporalMode, temporalItems, visibleItems]);

    const selectedSessions = useMemo(() => {
        if (!selectedItemTag || hiddenItemKeys.has(normalize(selectedItemTag))) return [];
        const sessions: UpsellSession[] = [];
        summaries.forEach(({ summary }) => {
            const matchingSessions = Object.entries(summary.withSessionId || {})
                .find(([tag]) => normalize(tag) === normalize(selectedItemTag))?.[1] || [];
            matchingSessions.forEach(session => {
                if (session.sessionId) sessions.push(session);
            });
        });
        const uniqueSessions = Array.from(new Map(sessions.map(session =>
            [`${session.applicationId}|${session.sessionId}`, session]
        )).values());
        return uniqueSessions.sort((a, b) => {
            const direction = oldestFirst ? 1 : -1;
            return direction * (new Date(a.callTime).getTime() - new Date(b.callTime).getTime());
        });
    }, [summaries, selectedItemTag, oldestFirst, hiddenItemKeys]);

    const openSessions = (itemName: string, itemTag?: string) => {
        setSelectedItem(itemName);
        setSelectedItemTag(itemTag || items.find(item => item.name === itemName)?.key || itemName);
        setOldestFirst(false);
        setPage(0);
    };
    const pageCount = Math.max(1, Math.ceil(selectedSessions.length / PAGE_SIZE));
    const visibleSessions = selectedSessions.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
    const axisBottom = {
        tickSize: 0,
        tickPadding: 10,
        tickRotation: temporalMode === 'hours' ? -35 : 0,
        legend: temporalMode === 'days' ? 'Day of week' : 'Hour of day',
        legendPosition: 'middle' as const,
        legendOffset: temporalMode === 'hours' ? 58 : 42
    };

    return (
        <Page>
            <PageHeader>
                <div>
                    <Eyebrow>AI ORDERING ANALYTICS</Eyebrow>
                    <PageTitle>Upsell Analytics</PageTitle>
                    <PageDescription>See which add-ons are selling and when customers accept them.</PageDescription>
                </div>
                <HeaderActions>
                    <BackButton onClick={onBack} type="button"><span aria-hidden="true">←</span> Back to analytics</BackButton>
                    <FilterButton type="button" aria-haspopup="dialog" aria-expanded={isFilterOpen} onClick={() => { setDraftExcludedItemKeys(new Set(excludedItemKeys)); setIsFilterOpen(true); }}>
                        Filter items{excludedItemKeys.size > 0 ? ` (${excludedItemKeys.size} excluded)` : ''}
                    </FilterButton>
                </HeaderActions>
            </PageHeader>

            <KpiGrid>
                <KpiCard>
                    <KpiLabel>Upsell success rate</KpiLabel>
                    {isLoading ? <LoadingBar style={{ height: 34, width: '42%' }} /> : <KpiValue>{Math.ceil(upsellSuccessRate)}%</KpiValue>}
                    {isLoading ? <LoadingBar style={{ height: 13, width: '82%' }} /> : <KpiFootnote>{successfulUpsells.toLocaleString()} successful upsells from {upsellAttempts.toLocaleString()} attempts</KpiFootnote>}
                </KpiCard>
                <KpiCard>
                    <KpiLabel>Upsold items</KpiLabel>
                    {isLoading ? <LoadingBar style={{ height: 34, width: '42%' }} /> : <KpiValue>{successfulUpsells.toLocaleString()}</KpiValue>}
                    {isLoading ? <LoadingBar style={{ height: 13, width: '55%' }} /> : <KpiFootnote>successful upsells</KpiFootnote>}
                </KpiCard>
                <KpiCard
                    onMouseEnter={() => setIsRevenueTooltipVisible(true)}
                    onMouseLeave={() => setIsRevenueTooltipVisible(false)}
                    onMouseMove={event => {
                        const bounds = event.currentTarget.getBoundingClientRect();
                        if (revenueTooltipRef.current) {
                            revenueTooltipRef.current.style.left = `${event.clientX - bounds.left}px`;
                            revenueTooltipRef.current.style.top = `${event.clientY - bounds.top - 42}px`;
                        }
                    }}
                    aria-describedby="upsell-revenue-tooltip"
                >
                    <RevenueTooltip id="upsell-revenue-tooltip" ref={revenueTooltipRef} role="tooltip" $visible={isRevenueTooltipVisible}>
                        Based on the estimated values below
                    </RevenueTooltip>
                    <KpiLabel>Total upsell revenue</KpiLabel>
                    {isLoading ? <LoadingBar style={{ height: 34, width: '62%' }} /> : <KpiValue>{formatCurrency(totalRevenue)}</KpiValue>}
                    {isLoading ? <LoadingBar style={{ height: 13, width: '88%' }} /> : <KpiFootnote>{formatCurrency(averageUpsellRevenue)} average revenue per successful upsell</KpiFootnote>}
                </KpiCard>
                <KpiCard>
                    <KpiLabel>Average upsell revenue per location</KpiLabel>
                    {isLoading ? <LoadingBar style={{ height: 34, width: '62%' }} /> : <KpiValue>{formatCurrency(averageRevenuePerLocation)}</KpiValue>}
                    {isLoading ? <LoadingBar style={{ height: 13, width: '72%' }} /> : <KpiFootnote>Across {selectedLocationCount.toLocaleString()} selected {selectedLocationCount === 1 ? 'location' : 'locations'}</KpiFootnote>}
                </KpiCard>
            </KpiGrid>

            <ContentGrid>
                <Card>
                    <CardHeader>
                        <div><CardTitle>Upsold items</CardTitle><CardSubtitle>Items ranked by frequency and revenue</CardSubtitle></div>
                    </CardHeader>
                    <ItemTableWrap>
                        <ItemTable>
                            <thead><tr>
                                <th>Item</th>
                                <th><SortHeaderButton type="button" aria-label="Sort by item frequency" aria-pressed={sortMode.startsWith('count-')} onClick={() => setSortMode(mode => mode === 'count-desc' ? 'count-asc' : 'count-desc')}>Quantity sold <SortIndicator>{sortMode.startsWith('count-') ? (sortMode === 'count-desc' ? '↓' : '↑') : '↕'}</SortIndicator></SortHeaderButton></th>
                                <th><SortHeaderButton type="button" aria-label="Sort by dollar value" aria-pressed={sortMode.startsWith('revenue-')} onClick={() => setSortMode(mode => mode === 'revenue-desc' ? 'revenue-asc' : 'revenue-desc')}>Revenue <SortIndicator>{sortMode.startsWith('revenue-') ? (sortMode === 'revenue-desc' ? '↓' : '↑') : '↕'}</SortIndicator></SortHeaderButton></th>
                                <th title="Adjust to match actuals">Estimated value</th>
                            </tr></thead>
                            <tbody>
                                {isLoading ? <tr><td colSpan={4}><LoadingStack>{[0, 1, 2, 3, 4].map(index => <LoadingBar key={index} style={{ width: `${92 - index * 7}%` }} />)}</LoadingStack></td></tr> : visibleItems.length ? visibleItems.map(item => {
                                    const itemKey = normalize(item.key);
                                    const configuredValues = configuredValuesByItem.get(itemKey) || [];
                                    const distinctValues = Array.from(new Set(configuredValues));
                                    const hasMixedValues = distinctValues.length > 1;
                                    const savedValue = distinctValues.length === 1 ? distinctValues[0] : null;
                                    const inputValue = Object.prototype.hasOwnProperty.call(valueDrafts, itemKey)
                                        ? valueDrafts[itemKey]
                                        : savedValue === null ? '' : String(savedValue);
                                    const saveValue = async (event: React.MouseEvent<HTMLButtonElement>) => {
                                        event.stopPropagation();
                                        const parsedValue = Number(inputValue);
                                        if (!Number.isFinite(parsedValue) || parsedValue < 0) {
                                            setSaveErrors(current => ({ ...current, [itemKey]: 'Enter a valid amount of 0 or more.' }));
                                            return;
                                        }
                                        setSavingValueTag(itemKey);
                                        setSaveErrors(current => ({ ...current, [itemKey]: '' }));
                                        try {
                                            await onSaveItemValue(item.key, parsedValue);
                                            setValueDrafts(current => {
                                                const next = { ...current };
                                                delete next[itemKey];
                                                return next;
                                            });
                                        } catch (error) {
                                            setSaveErrors(current => ({ ...current, [itemKey]: error instanceof Error ? error.message : 'Could not save this value.' }));
                                        } finally {
                                            setSavingValueTag(null);
                                        }
                                    };
                                    return (
                                    <tr key={item.key} onClick={() => openSessions(item.name, item.key)} tabIndex={0} onKeyDown={event => event.key === 'Enter' && openSessions(item.name, item.key)}>
                                        <td><ItemLink>{item.name}</ItemLink></td>
                                        <td>{item.count.toLocaleString()}</td>
                                        <td>{formatCurrency(item.revenue)}</td>
                                        <td title="Adjust to match actuals" onClick={event => event.stopPropagation()}>
                                            <ValueEditor>
                                                <CurrencyInput
                                                    aria-label={`Estimated value for ${item.name}`}
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    placeholder={hasMixedValues ? 'Mixed' : '$0.00'}
                                                    value={inputValue}
                                                    disabled={isLoading || savingValueTag !== null}
                                                    onChange={event => setValueDrafts(current => ({ ...current, [itemKey]: event.target.value }))}
                                                    onKeyDown={event => event.key === 'Enter' && event.stopPropagation()}
                                                />
                                                <SaveValueButton type="button" disabled={isLoading || savingValueTag !== null} onClick={saveValue}>
                                                    {savingValueTag === itemKey ? 'Saving…' : 'Save'}
                                                </SaveValueButton>
                                            </ValueEditor>
                                            {saveErrors[itemKey] && <SaveError role="alert">{saveErrors[itemKey]}</SaveError>}
                                        </td>
                                    </tr>
                                    );
                                }) : <tr><td colSpan={4}><EmptyState>{items.length ? 'All items are excluded by the current filters.' : 'No upsold items were returned for this date range.'}</EmptyState></td></tr>}
                            </tbody>
                        </ItemTable>
                    </ItemTableWrap>
                </Card>

                <ChartRow>
                    <Card>
                        <CardHeader>
                            <div><CardTitle>Upsell timing</CardTitle><CardSubtitle>Compare your most popular items over time</CardSubtitle></div>
                            <SegmentControl aria-label="Choose temporal breakdown">
                                <SegmentButton type="button" $active={temporalMode === 'days'} onClick={() => setTemporalMode('days')}>Days of week</SegmentButton>
                                <SegmentButton type="button" $active={temporalMode === 'hours'} onClick={() => setTemporalMode('hours')}>Hours of day</SegmentButton>
                            </SegmentControl>
                        </CardHeader>
                        <ChartArea>
                            {isLoading ? <ChartLoading><LoadingBar style={{ width: '74%' }} /><LoadingBar style={{ width: '88%' }} /><LoadingBar style={{ width: '62%' }} /><LoadingBar style={{ width: '81%' }} /></ChartLoading> : temporalItems.length ? <GroupedChartLayout>
                                <GroupedChartPlot>
                                    <ResponsiveBar
                                        data={temporalData}
                                        keys={temporalItems.map(item => item.name)}
                                        indexBy="bucket"
                                        groupMode="grouped"
                                        margin={{ top: 12, right: 16, bottom: temporalMode === 'hours' ? 76 : 60, left: 44 }}
                                        padding={0.25}
                                        colors={({ id }) => ITEM_COLORS[Math.max(0, temporalItems.findIndex(item => item.name === String(id))) % ITEM_COLORS.length]}
                                        borderRadius={3}
                                        axisLeft={{ tickSize: 0, tickPadding: 8, legend: 'Successful upsells', legendPosition: 'middle', legendOffset: -34 }}
                                        axisBottom={axisBottom}
                                        enableGridX={false}
                                        enableLabel={false}
                                        theme={nivoChartTheme}
                                        onClick={bar => openSessions(String(bar.id), items.find(item => item.name === String(bar.id))?.key)}
                                        role="application"
                                        ariaLabel="Grouped chart of upsold items by time"
                                    />
                                </GroupedChartPlot>
                                <LegendRow>
                                    {temporalItems.map((item, index) => (
                                        <LegendItem key={item.key}>
                                            <LegendSwatch $color={ITEM_COLORS[index % ITEM_COLORS.length]} />
                                            <LegendLabel>{item.name}</LegendLabel>
                                        </LegendItem>
                                    ))}
                                </LegendRow>
                            </GroupedChartLayout> : <EmptyState>No temporal upsell data for this date range.</EmptyState>}
                        </ChartArea>
                    </Card>

                    <Card>
                        <CardHeader><div><CardTitle>Upsold items</CardTitle><CardSubtitle>Top 10 by successful upsells</CardSubtitle></div></CardHeader>
                        <TopItemsChart>
                            {isLoading ? <ChartLoading><LoadingBar style={{ width: '82%' }} /><LoadingBar style={{ width: '68%' }} /><LoadingBar style={{ width: '92%' }} /><LoadingBar style={{ width: '57%' }} /><LoadingBar style={{ width: '73%' }} /></ChartLoading> : topItems.length ? <ResponsiveBar
                                data={[...topItems].reverse().map(item => ({ item: item.name, count: item.count }))}
                                keys={['count']}
                                indexBy="item"
                                layout="horizontal"
                                margin={{ top: 4, right: 24, bottom: 36, left: 112 }}
                                padding={0.24}
                                colors={() => '#0284C7'}
                                borderRadius={3}
                                axisBottom={{ tickSize: 0, tickPadding: 8, legend: 'Number of upsells', legendPosition: 'middle', legendOffset: 30 }}
                                axisLeft={{ tickSize: 0, tickPadding: 8, format: value => truncateChartLabel(String(value)) }}
                                enableGridY={false}
                                enableLabel={false}
                                theme={nivoChartTheme}
                                tooltip={({ indexValue, value }) => (
                                    <div style={{ background: '#fff', borderRadius: 7, boxShadow: '0 4px 16px rgba(15, 23, 42, .16)', color: '#1e293b', fontSize: 12, padding: '9px 12px' }}>
                                        <strong>{String(indexValue)}</strong>
                                        <div>{Number(value).toLocaleString()} upsells</div>
                                    </div>
                                )}
                                onClick={bar => openSessions(String(bar.indexValue), items.find(item => item.name === String(bar.indexValue))?.key)}
                                role="application"
                                ariaLabel="Top ten upsold items by quantity"
                            /> : <EmptyState>No upsold items for this date range.</EmptyState>}
                        </TopItemsChart>
                    </Card>
                </ChartRow>
            </ContentGrid>

            {isFilterOpen && <ModalOverlay onClick={() => setIsFilterOpen(false)}>
                <FilterModal role="dialog" aria-modal="true" aria-labelledby="filter-modal-title" onClick={event => event.stopPropagation()}>
                    <ModalHeader>
                        <div>
                            <CardTitle id="filter-modal-title">Filter upsold items</CardTitle>
                            <CardSubtitle>Select items to exclude from the dashboard.</CardSubtitle>
                        </div>
                        <CloseButton type="button" aria-label="Close filters" onClick={() => setIsFilterOpen(false)}>×</CloseButton>
                    </ModalHeader>
                    <FilterList>
                        {items.length ? items.map(item => {
                            const itemKey = normalize(item.key);
                            return (
                                <FilterOption key={item.key}>
                                    <input
                                        type="checkbox"
                                        checked={draftExcludedItemKeys.has(itemKey)}
                                        onChange={() => setDraftExcludedItemKeys(current => {
                                            const next = new Set(current);
                                            if (next.has(itemKey)) next.delete(itemKey);
                                            else next.add(itemKey);
                                            return next;
                                        })}
                                    />
                                    <span>{item.name}</span>
                                    <FilterOptionCount>{item.count.toLocaleString()}</FilterOptionCount>
                                </FilterOption>
                            );
                        }) : <EmptyState>No items are available to filter.</EmptyState>}
                    </FilterList>
                    <FilterFooter>
                        <SortToggle type="button" onClick={() => setDraftExcludedItemKeys(new Set())}>Clear exclusions</SortToggle>
                        <PageButtons>
                            <PageButton type="button" onClick={() => setIsFilterOpen(false)}>Cancel</PageButton>
                            <ApplyFilterButton type="button" onClick={() => { setExcludedItemKeys(new Set(draftExcludedItemKeys)); setIsFilterOpen(false); }}>Apply filters</ApplyFilterButton>
                        </PageButtons>
                    </FilterFooter>
                </FilterModal>
            </ModalOverlay>}

            {selectedItem && <ModalOverlay onClick={() => setSelectedItem(null)}>
                <SessionModal role="dialog" aria-modal="true" aria-labelledby="session-modal-title" onClick={event => event.stopPropagation()}>
                    <ModalHeader>
                        <div><CardTitle id="session-modal-title">{selectedItem} sessions</CardTitle><CardSubtitle>{selectedSessions.length.toLocaleString()} successful upsell sessions</CardSubtitle></div>
                        <CloseButton type="button" aria-label="Close sessions" onClick={() => setSelectedItem(null)}>×</CloseButton>
                    </ModalHeader>
                    <ModalTools>
                        <span>Sorted by {oldestFirst ? 'oldest first' : 'most recent first'}</span>
                        <SortToggle type="button" onClick={() => { setOldestFirst(value => !value); setPage(0); }}>
                            {oldestFirst ? 'Show most recent first' : 'Show oldest first'}
                        </SortToggle>
                    </ModalTools>
                    <SessionTableWrap>
                        <SessionTable>
                            <thead><tr><th>Call time</th><th>Application</th><th>Session</th></tr></thead>
                            <tbody>
                                {visibleSessions.map(session => (
                                    <tr key={`${session.applicationId}|${session.sessionId}`}>
                                        <td>{new Date(session.callTime).toLocaleString()}</td>
                                        <td>{(appNameMap && appNameMap[session.applicationId]) || session.applicationId}</td>
                                        <td><SessionLink href={`/v/apps/${encodeURIComponent(session.applicationId)}/analytics/sessions?sessionId=${encodeURIComponent(session.sessionId)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`} target="_blank" rel="noopener noreferrer">View Transcript</SessionLink></td>
                                    </tr>
                                ))}
                                {!visibleSessions.length && <tr><td colSpan={3}><EmptyState>No session records are available for this item.</EmptyState></td></tr>}
                            </tbody>
                        </SessionTable>
                    </SessionTableWrap>
                    <ModalFooter>
                        <span>Page {page + 1} of {pageCount}</span>
                        <PageButtons>
                            <PageButton type="button" disabled={page === 0} onClick={() => setPage(value => Math.max(0, value - 1))}>Previous</PageButton>
                            <PageButton type="button" disabled={page + 1 >= pageCount} onClick={() => setPage(value => Math.min(pageCount - 1, value + 1))}>Next</PageButton>
                        </PageButtons>
                    </ModalFooter>
                </SessionModal>
            </ModalOverlay>}
        </Page>
    );
};

const Page = styled.div`
    background: #f4f6f9;
    color: #1e293b;
    min-height: 100%;
    padding: 28px;
    @media (max-width: 768px) { padding: 16px; }
`;
const PageHeader = styled.header`display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 24px; @media (max-width: 600px) { flex-direction: column-reverse; align-items: stretch; }`;
const HeaderActions = styled.div`align-items: flex-end; display: flex; flex-direction: column; gap: 8px; @media (max-width: 600px) { align-items: flex-start; width: 100%; }`;
const Eyebrow = styled.div`color: #0284c7; font-size: 11px; font-weight: 700; letter-spacing: .1em; margin-bottom: 6px;`;
const PageTitle = styled.h1`color: #1e293b; font-size: 28px; font-weight: 700; margin: 0;`;
const PageDescription = styled.p`color: #64748b; font-size: 14px; margin: 7px 0 0;`;
const BackButton = styled.button`background: #fff; border: 1px solid #dbe3eb; border-radius: 9px; color: #334155; cursor: pointer; font: inherit; font-size: 13px; font-weight: 600; padding: 10px 14px; white-space: nowrap; &:hover { background: #f8fafc; border-color: #94a3b8; } span { font-size: 18px; margin-right: 6px; vertical-align: -1px; }`;
const FilterButton = styled(BackButton)`color: #0369a1; span { font-size: 14px; }`;
const KpiGrid = styled.div`display: grid; gap: 16px; grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 18px; @media (max-width: 1100px) { grid-template-columns: repeat(2, minmax(0, 1fr)); } @media (max-width: 600px) { grid-template-columns: 1fr; }`;
const KpiCard = styled.section`background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 2px 8px rgba(15, 23, 42, .04); padding: 20px 22px; position: relative;`;
const KpiLabel = styled.div`color: #64748b; font-size: 13px; font-weight: 600;`;
const KpiValue = styled.div`color: #1e293b; font-size: 32px; font-weight: 700; letter-spacing: -.03em; margin-top: 9px;`;
const KpiFootnote = styled.div`color: #64748b; font-size: 12px; margin-top: 5px;`;
const ContentGrid = styled.div`display: grid; gap: 18px;`;
const ChartRow = styled.div`display: grid; gap: 18px; grid-template-columns: minmax(0, 1.4fr) minmax(340px, 1fr); @media (max-width: 1080px) { grid-template-columns: 1fr; }`;
const Card = styled.section`background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; box-shadow: 0 2px 8px rgba(15, 23, 42, .04); min-width: 0; padding: 18px;`;
const CardHeader = styled.div`align-items: center; display: flex; gap: 14px; justify-content: space-between; margin-bottom: 16px; @media (max-width: 650px) { align-items: flex-start; flex-direction: column; }`;
const CardTitle = styled.h2`color: #1e293b; font-size: 15px; font-weight: 700; margin: 0;`;
const CardSubtitle = styled.div`color: #64748b; font-size: 12px; margin-top: 5px;`;
const ItemTableWrap = styled.div`height: 360px; max-width: 100%; overflow: auto;`;
const ItemTable = styled.table`border-collapse: collapse; width: 100%; th { background: #fff; color: #64748b; font-size: 11px; font-weight: 700; letter-spacing: .04em; padding: 10px 12px; position: sticky; text-align: left; text-transform: uppercase; top: 0; z-index: 1; } td { border-top: 1px solid #eef2f6; color: #334155; font-size: 13px; padding: 12px; } th:nth-child(n+2), td:nth-child(n+2) { text-align: right; } th:nth-child(3), td:nth-child(3) { width: 110px; white-space: nowrap; } th:nth-child(4), td:nth-child(4) { padding-left: 4px; width: 190px; } tbody tr { cursor: pointer; } tbody tr:hover { background: #f8fafc; }`;
const SortHeaderButton = styled.button`align-items: center; background: transparent; border: 0; color: inherit; cursor: pointer; display: inline-flex; font: inherit; font-weight: inherit; gap: 5px; padding: 0; &:hover { color: #0369a1; }`;
const SortIndicator = styled.span`color: #0284c7; font-size: 13px; line-height: 1;`;
const ItemLink = styled.span`color: #0369a1; font-weight: 600;`;
const EmptyState = styled.div`align-items: center; color: #94a3b8; display: flex; font-size: 13px; justify-content: center; min-height: 120px; padding: 14px; text-align: center;`;
const LoadingBar = styled.div`animation: upsell-loading 1.35s ease-in-out infinite; background: linear-gradient(90deg, #e8edf3 25%, #f5f7fa 45%, #e8edf3 65%); background-size: 300% 100%; border-radius: 6px; height: 14px; @keyframes upsell-loading { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }`;
const LoadingStack = styled.div`display: flex; flex-direction: column; gap: 18px; padding: 22px 8px;`;
const ChartLoading = styled.div`align-content: center; align-items: flex-start; display: flex; flex-direction: column; gap: 24px; height: 100%; justify-content: center; padding: 28px 14px;`;
const SegmentControl = styled.div`background: #f1f5f9; border-radius: 9px; display: inline-flex; gap: 3px; padding: 3px;`;
const SegmentButton = styled.button<{ $active: boolean }>`background: ${props => props.$active ? '#fff' : 'transparent'}; border: 0; border-radius: 7px; box-shadow: ${props => props.$active ? '0 1px 3px rgba(15, 23, 42, .12)' : 'none'}; color: ${props => props.$active ? '#0369a1' : '#64748b'}; cursor: pointer; font: inherit; font-size: 11px; font-weight: 600; padding: 8px 10px; white-space: nowrap;`;
const ChartArea = styled.div`height: 360px; @media (max-width: 650px) { height: 320px; }`;
const GroupedChartLayout = styled.div`display: flex; flex-direction: column; height: 100%; min-height: 0;`;
const GroupedChartPlot = styled.div`flex: 1 1 auto; min-height: 0;`;
const LegendRow = styled.div`align-items: center; display: flex; flex: 0 0 auto; flex-wrap: wrap; gap: 6px 20px; justify-content: flex-start; padding: 4px 0 2px;`;
const LegendItem = styled.div`align-items: center; display: flex; flex: 0 1 auto; gap: 6px; min-width: 0;`;
const LegendSwatch = styled.span<{ $color: string }>`background: ${props => props.$color}; border-radius: 3px; flex: 0 0 9px; height: 9px;`;
const LegendLabel = styled.span`color: #64748b; font-size: 11px; line-height: 1.25; overflow-wrap: anywhere;`;
const TopItemsChart = styled.div`height: 380px;`;
const ModalOverlay = styled.div`align-items: center; background: rgba(15, 23, 42, .52); display: flex; inset: 0; justify-content: center; padding: 20px; position: fixed; z-index: 10000;`;
const FilterModal = styled.div`background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(15, 23, 42, .28); display: flex; flex-direction: column; max-height: 85vh; max-width: 560px; overflow: hidden; padding: 20px; width: 100%;`;
const FilterList = styled.div`border-bottom: 1px solid #eef2f6; border-top: 1px solid #eef2f6; flex: 1 1 auto; margin: 4px 0 12px; min-height: 120px; overflow: auto;`;
const FilterOption = styled.label`align-items: center; border-bottom: 1px solid #f1f5f9; color: #334155; cursor: pointer; display: flex; font-size: 13px; gap: 10px; padding: 12px 4px; &:last-child { border-bottom: 0; } input { accent-color: #0284c7; height: 16px; width: 16px; }`;
const FilterOptionCount = styled.span`color: #64748b; font-size: 12px; margin-left: auto;`;
const FilterFooter = styled.div`align-items: center; display: flex; flex: 0 0 auto; justify-content: space-between;`;
const SessionModal = styled.div`background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(15, 23, 42, .28); display: flex; flex-direction: column; max-height: 90vh; max-width: 980px; overflow: hidden; padding: 20px; width: 100%;`;
const ModalHeader = styled.div`align-items: flex-start; display: flex; justify-content: space-between; margin-bottom: 16px;`;
const CloseButton = styled.button`background: transparent; border: 0; color: #64748b; cursor: pointer; font-size: 26px; line-height: 1;`;
const ModalTools = styled.div`align-items: center; color: #64748b; display: flex; font-size: 12px; justify-content: space-between; margin: 10px 0;`;
const SortToggle = styled.button`background: transparent; border: 0; color: #0369a1; cursor: pointer; font: inherit; font-weight: 600;`;
const SessionTableWrap = styled.div`flex: 1 1 auto; min-height: 0; overflow: auto;`;
const SessionTable = styled.table`border-collapse: collapse; width: 100%; th { background: #f8fafc; color: #64748b; font-size: 11px; padding: 10px; position: sticky; text-align: left; top: 0; z-index: 1; } td { border-top: 1px solid #eef2f6; color: #334155; font-size: 12px; padding: 11px 10px; }`;
const SessionLink = styled.a`color: #0369a1; font-weight: 600; text-decoration: none; &:hover { text-decoration: underline; }`;
const ModalFooter = styled.div`align-items: center; border-top: 1px solid #eef2f6; color: #64748b; display: flex; flex: 0 0 auto; font-size: 12px; justify-content: space-between; margin-top: 12px; padding-top: 12px;`;
const PageButtons = styled.div`display: flex; gap: 8px;`;
const PageButton = styled.button`background: #fff; border: 1px solid #cbd5e1; border-radius: 7px; color: #334155; cursor: pointer; font: inherit; padding: 7px 10px; &:disabled { color: #94a3b8; cursor: default; }`;
const ApplyFilterButton = styled(PageButton)`background: #0284c7; border-color: #0284c7; color: #fff; font-weight: 600; &:hover { background: #0369a1; }`;

export default UpsellAnalyticsDashboard;
